import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, LogOut } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import vcmLogo from "@/assets/vcm-logo-small.webp";
import NotificationBell from "./NotificationBell";
import { InstallButton } from "./pwa/InstallButton";

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [mobileNotifCount, setMobileNotifCount] = useState(0);
  const [pendingCallRequests, setPendingCallRequests] = useState(0);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsLoggedIn(!!session);
      setUserId(session?.user?.id || null);
      
      if (session?.user) {
        const { data: roles } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .eq('role', 'admin')
          .single();
        
        setIsAdmin(!!roles);
      } else {
        setIsAdmin(false);
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
      setUserId(session?.user?.id || null);
      
      if (session?.user) {
        setTimeout(async () => {
          const { data: roles } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.user.id)
            .eq('role', 'admin')
            .single();
          
          setIsAdmin(!!roles);
        }, 0);
      } else {
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Track notification count for mobile badge
  useEffect(() => {
    if (!userId) {
      setMobileNotifCount(0);
      return;
    }

    const loadNotifCount = async () => {
      try {
        // Count unread messages
        const { count: msgCount } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('recipient_id', userId)
          .eq('is_read', false);

        // Count recent battle matches (last 7 days)
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        const { data: matchData } = await supabase
          .from('battle_matches')
          .select(`
            id,
            updated_at,
            request_1:battle_requests!request_1_id(creator_id),
            request_2:battle_requests!request_2_id(creator_id)
          `)
          .gte('updated_at', sevenDaysAgo.toISOString());

        const matchCount = (matchData || []).filter((match: any) => {
          const creator1Id = match.request_1?.creator_id;
          const creator2Id = match.request_2?.creator_id;
          return creator1Id === userId || creator2Id === userId;
        }).length;

        setMobileNotifCount((msgCount || 0) + matchCount);
      } catch (error) {
        console.error('Error loading notification count:', error);
      }
    };

    loadNotifCount();

    // Subscribe to changes
    const channel = supabase
      .channel('mobile-notif-count')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, loadNotifCount)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'battle_matches' }, loadNotifCount)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // Track pending call requests for admin badge
  useEffect(() => {
    if (!isAdmin) {
      setPendingCallRequests(0);
      return;
    }

    const loadPendingCallRequests = async () => {
      try {
        const { count } = await supabase
          .from('call_requests')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending');
        
        setPendingCallRequests(count || 0);
      } catch (error) {
        console.error('Error loading pending call requests:', error);
      }
    };

    loadPendingCallRequests();

    // Subscribe to changes
    const channel = supabase
      .channel('admin-call-requests')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'call_requests' }, loadPendingCallRequests)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success("Signed out successfully");
      navigate("/");
    } catch (error) {
      toast.error("Failed to sign out");
    }
  };

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Why VCM", path: "/why" },
    { name: "Success Stories", path: "/success" },
    { name: "The Program", path: "/program" },
    { name: "360° Management", path: "/talent-management" },
    { name: "Battle Request", path: "/battle-request" },
    { name: "Battle Stats", path: "/stats" },
    { name: "Creator Portal", path: "/creator-portal" },
    { name: "FAQ", path: "/faq" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border pt-[env(safe-area-inset-top)]">
      <div className="container mx-auto px-4 py-4 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src={vcmLogo} 
              alt="Viral Check Media" 
              width="140" 
              height="98"
              fetchPriority="high"
              className="h-10 md:h-12 w-auto brightness-110 contrast-125" 
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === link.path ? "text-primary" : "text-muted-foreground"
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <InstallButton variant="icon" />
            {isLoggedIn ? (
              <>
                {userId && <NotificationBell userId={userId} />}
                {isAdmin && (
                  <Link to="/admin" className="relative">
                    <Button variant="outline" size="lg">
                      Admin Dashboard
                    </Button>
                    {pendingCallRequests > 0 && (
                      <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-medium">
                        {pendingCallRequests > 9 ? '9+' : pendingCallRequests}
                      </span>
                    )}
                  </Link>
                )}
                <Button variant="ghost" size="lg" onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              </>
            ) : (
              <Link to="/apply">
                <Button variant="premium" size="lg">
                  Apply Now
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-foreground relative"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            {!mobileMenuOpen && isLoggedIn && mobileNotifCount > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-medium">
                {mobileNotifCount > 9 ? '9+' : mobileNotifCount}
              </span>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block text-sm font-medium transition-colors hover:text-primary ${
                  location.pathname === link.path ? "text-primary" : "text-muted-foreground"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            {isLoggedIn ? (
              <>
                {userId && (
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-muted-foreground">Notifications</span>
                    <NotificationBell userId={userId} />
                  </div>
                )}
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">Install App</span>
                  <InstallButton variant="icon" />
                </div>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="relative block">
                    <Button variant="outline" size="lg" className="w-full">
                      Admin Dashboard
                      {pendingCallRequests > 0 && (
                        <span className="ml-2 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-medium">
                          {pendingCallRequests > 9 ? '9+' : pendingCallRequests}
                        </span>
                      )}
                    </Button>
                  </Link>
                )}
                <Button 
                  variant="ghost" 
                  size="lg" 
                  className="w-full"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-muted-foreground">Install App</span>
                  <InstallButton variant="icon" />
                </div>
                <Link to="/apply" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="premium" size="lg" className="w-full">
                    Apply Now
                  </Button>
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
